import { useEffect, useState } from "react";
import Switch from "../../../../components/common/Switch";
import Button from "../../../../components/common/button";
import DataTable from "react-data-table-component";
import {
  ICategoryComponentProps,
  ITableColumn,
  IVideos,
} from "../../../../utils/types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "../../../../components/common/DragRow";
import { DragSvg, TrashSvg } from "../../../../icons";
import { setDragOnlyInStaging } from "../../../../utils/helpers";
import AddOurStortyModal from "./create-our-story";
import useGetEligibilityCriteriaQuery from "../../../../hooks/use-get-eligibility-criteria";
import Spinner from "../../../../components/common/Spinner";
import EditOurStory from "../../../../components/common/Activity/edit-our-story";
import { queryClient } from "../../../../App";
import useGetOurStory from "../../../../hooks/use-get-our-story";
import classNames from "classnames";
import Modal from "../../../../components/common/Modal";
import useDeleteOurStory from "../../../../hooks/use-delete-our-story";
import { toast } from "react-toastify";
import useUpdateOurStoryOrderAndStatus from "../../../../hooks/use-update-our-story-order-status";
import useUpdateOurStoryStatus from "../../../../hooks/use-update-our-story-status";
import { useGetUserPermission } from "../../../../context/AuthStaffUserContext";

type IFormData = {
  videos: IVideos[];
};

const OurStory = ({ data, loading, onSave }: ICategoryComponentProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [maxVideoDuration, setMaxVideoDuration] = useState<number>(0);
  const [maxImages, setMaxImages] = useState<number>(0);


  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openDeleteModal = () => setDeleteModal(true);
  const closeDeleteModal = () => setDeleteModal(false);
  // const [InitialFormData, setInitialFormData] = useState<IFormData>({videos:[]});

  const methods = useForm<IFormData>({ defaultValues: { videos: [] } });
  const { handleSubmit } = methods;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    fields: videoFields,
    // append: appendAlarmField,
    remove: removeVideoField,
    replace: replaceVideoFields,
  } = useFieldArray({
    name: "videos",
    control: methods.control,
    keyName: "customId",
  });

  const {
    data: eligibilityCriteriaData,
    isLoading: eligibilityCriteriaLoading,
  } = useGetEligibilityCriteriaQuery();
  const { data: ourStoryData, isPending: getOurStoryLoading } =
    useGetOurStory();
  const { mutate: deleteOurStory, isPending: deleteLoader } =
    useDeleteOurStory();
  const { mutate: updateOrderStatus } = useUpdateOurStoryOrderAndStatus();
  const { mutate: updateOrderActiveStatus } = useUpdateOurStoryStatus();

  // const eligibilityOptions =
  //   eligibilityCriteriaData?.result?.data
  //     .sort((b, a) => b.viewRank - a.viewRank)
  //     .flatMap((x) => x.options || []) || [];

  useEffect(() => {
    if (ourStoryData?.result?.data?.items) {
      const sortedVideos = [...ourStoryData.result.data.items].sort(
        (a, b) => a?.orderKey - b?.orderKey
      );

      replaceVideoFields(sortedVideos);
      setVideoData({
        categoryName,
        items: sortedVideos,
      });
    }
    if (ourStoryData?.result?.data?.maxVideoDuration) {
      setMaxVideoDuration(ourStoryData?.result?.data?.maxVideoDuration);
    }
    if (ourStoryData?.result?.data?.maxImagesForHomeTv) {
      setMaxImages(ourStoryData?.result?.data?.maxImagesForHomeTv);
    }
  }, [ourStoryData, replaceVideoFields]);

  const categoryName = data?.categoryName || "";

  const [videoData, setVideoData] = useState(() => ({
    categoryName,
    items:
      [...(videoFields as IVideos[])]?.sort(
        (a, b) => a.orderKey - b.orderKey
      ) ?? [], // Use subData directly or default to an empty array
  }));

  useEffect(() => {
    // Sort and update videoData.items whenever videoFields changes
    const sorted = [...videoFields].sort((a, b) => a.orderKey - b.orderKey);
    setVideoData((prev) => ({
      ...prev,
      items: sorted,
    }));
  }, [videoFields]);

  const getTableDataIndex = (id: string) => {
    return videoFields.findIndex((item) => item._id === id);
  };

  const onDropComplete = () => {
    const updatedItems = [...videoData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));
    setVideoData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));
    const updateData = updatedItems.map((ele) => ({
      id: ele._id,
      orderKey: ele.orderKey,
      isSelected: ele.isSelected,
    }));
    updateOrderStatus(updateData, {
      onSuccess: (res) => {
        // toast.success(res?.result?.message);
      },
      onError: (error) => {
        const message =
          error.response?.data.result.message || "An unexpected error occurred";
        console.log({ message, error });

        // toast.error(message);
      },
    });
  };

  const onSubmit = (submitData: IFormData) => {
    const orderKeyMap = new Map(
      videoData.items.map((item, index) => [
        item._id,
        item.orderKey || index + 1,
      ])
    );

    const mergedData = submitData.videos
      .sort((a, b) => a?.orderKey - b?.orderKey)
      .map((item) => ({
        ...item,
        orderKey: orderKeyMap.get(item._id) ?? item.orderKey,
      }));
    onSave({
      categoryName: data?.categoryName,
      data: {
        ...data,
        videos: mergedData,
      },
    });
  };

  const handleDelete = (id: string) => {
    deleteOurStory(
      { id },
      {
        onSuccess: (res) => {
          removeVideoField(
            videoFields.findIndex((ele) => ele?._id === selectedId)
          );
          toast.success(res?.result?.message);
          closeDeleteModal();
        },
        onError: (error) => {
          closeDeleteModal();
          const message =
            error.response?.data.result.message ||
            "An unexpected error occurred";
          toast.error(message);
        },
      }
    );
  };

  const handleActiveStatus = (
    id: string,
    currentStatus: boolean | undefined,
    eligibilityCriteriaId: string | undefined,
    valueId: string | string[] | undefined
  ) => {
    updateOrderActiveStatus(
      { id: id, isSelected: currentStatus, eligibilityCriteriaId, valueId },
      {
        onSuccess: (res) => {
          methods.setValue(
            `videos.${getTableDataIndex(id)}.isSelected`,
            currentStatus
          );
          toast.success(res?.result?.message);
          closeDeleteModal();
        },
        onError: (error) => {
          closeDeleteModal();
          const message =
            error.response?.data.result?.message ||
            "An unexpected error occurred";
          toast.error(message);
        },
      }
    );
  };  

  const {write: isEditPermission} = useGetUserPermission("tv-app");

  const tableColumns: ITableColumn<IVideos>[] = setDragOnlyInStaging<
    ITableColumn<IVideos>
  >([
    {
      name: "",
      cell: (Row, index) => (
        <DraggableRow
          index={index}
          moveRow={moveRow}
          onDropComplete={onDropComplete}
        >
          <DragSvg
            height={24}
            width={24}
            className="h-6 w-6 cursor-grab flex-none"
          />
        </DraggableRow>
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Image",
      cell: (Row) => {
        return (
          <div className="">
            <img src={Row.thumbnail} width={64} height={39} alt="thumbnail" />
          </div>
        );
      },
      sortable: true,
      width: "10%",
    },
    {
      name: "Name",
      cell: (Row) => (
        <span style={{ fontWeight: 400, fontSize: "14px" }}>
          {Row.name || ""}
        </span>
      ),
      sortable: true,
      width: "20%",
    },
    {
      name: 'Media Type',
      cell: (Row) => (

        <span>{Row?.images?.length > 0 ? 'Image' : 'Video'}</span>
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Guest Type",
      cell: (Row) => (
        <span style={{ fontWeight: 400, fontSize: "14px" }}>
          {eligibilityCriteriaData?.result?.data.find(
            (ele) => ele._id === Row.eligibilityCriteria
          )?.name || "-"}
        </span>
      ),
      sortable: true,
      width: "25%",
    },
    {
      name: "Enable/Disable",
      cell: (Row) => (
        <Switch
          name={`videos.${getTableDataIndex(Row._id)}.isSelected`}
          checked={videoFields.find((ele) => ele._id === Row._id)?.isSelected}
          onCheck={(isChecked) => {
            handleActiveStatus(
              Row._id,
              !isChecked,
              Row.eligibilityCriteria,
              Row.value
            );
          }}
          disabled={!isEditPermission}
        />
      ),
      sortable: true,
      center: true,
      width: "15%",
    },
    {
      name: "Action",
      cell: (Row) => (
        <div className="flex items-center gap-2">
          <div
            onClick={() => {
              setSelectedId(Row._id);
            }}
          >
            <EditOurStory
            disabled={!isEditPermission}
              data={
                videoData.items.find((ele) => ele._id === selectedId) || null
              }
              isEditVideo={!!selectedId}
              onClose={async () => {
                await queryClient.refetchQueries({
                  queryKey: ["hotel-our-story"],
                });
              }}
               maxVideoDuration={maxVideoDuration}
          maxImages={maxImages}
            />
          </div>
          <div
            onClick={
              getOurStoryLoading
                ? undefined
                : () => {
                    if(!isEditPermission) return;
                    setSelectedId(Row._id);
                    openDeleteModal();
                  }
            }
            className={classNames(
              "h-6 w-6 rounded-[3.63px] bg-[#E5272733] flex items-center justify-center",
              getOurStoryLoading || !isEditPermission ? "opacity-50" : "cursor-pointer"
            )}
          >
            <TrashSvg color="#E52727" />
          </div>
        </div>
      ),
      sortable: true,
      center: true,
      width: "10%",
    },
  ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!videoData?.items) return;

    const globalFromIndex = (currentPage - 1) * pageSize + fromIndex;
    const globalToIndex = (currentPage - 1) * pageSize + toIndex;

    const updatedData = [...videoData.items];
    const [movedItem] = updatedData.splice(globalFromIndex, 1);
    updatedData.splice(globalToIndex, 0, movedItem);

    const reOrdered = updatedData.map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setVideoData({
      ...videoData,
      items: reOrdered,
    });
  };

  return (
    <FormProvider {...methods}>
      <div>
        <h3 className="text-[22px] font-normal text-[#1E2F65]">Our Story</h3>
        <div className="">
          <Switch name="showAlarm" label="Our Story (Hide/Show) " disabled={!isEditPermission}/>
        </div>
        <div className="flex items-center justify-end w-full">
          <Button variant="primary" className="w-21" onClick={openModal} disabled={!isEditPermission}>
            Add
          </Button>
        </div>
        {/* <div className="h-full max-h-[calc(100vh-520px)] overflow-auto mt-[17px]"> */}
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            data={
              videoData?.items.sort((a, b) => a?.orderKey - b?.orderKey) || []
            }
            columns={tableColumns}
            // striped={true}
            // keyField="customId"
            persistTableHead
            pagination={videoFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
            progressPending={eligibilityCriteriaLoading || getOurStoryLoading}
            progressComponent={
              <div className="mt-10">
                <Spinner />
              </div>
            }
            onChangePage={(page) => setCurrentPage(page)}
            onChangeRowsPerPage={(perPage) => setPageSize(perPage)}
          />
        </DndProvider>
        {/* </div> */}

        {/* <DiscardSaveButtons
          onDiscard={() => {}}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        /> */}
        <div
          className={classNames("flex items-center justify-end gap-[18px]", "")}
        >
          <Button
            className={classNames("w-25.5", "mt-10")}
            variant="primary"
            loading={loading}
            disabled={loading || !isEditPermission}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </div>

        <Modal
          isOpen={deleteModal}
          closeModal={() => {
            closeDeleteModal();
            closeModal();
          }}
          title="Are you sure you want to delete this?"
          className="w-144.75"
        >
          <div className="flex items-center justify-end gap-[18px] mt-9">
            <Button className="w-25.5" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleDelete(selectedId!);
              }}
              loading={deleteLoader}
              disabled={deleteLoader}
              className="w-25.5"
              variant="primary"
            >
              Delete
            </Button>
          </div>
        </Modal>

        <AddOurStortyModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={data.categoryName}
          maxVideoDuration={maxVideoDuration}
          maxImages={maxImages}
        />
      </div>
    </FormProvider>
  );
};

export default OurStory;
